import React, { useState, useEffect, useRef, useContext } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { toast } from "react-hot-toast";
import { validatePhone } from '../utils/phoneValidator';
import { SocketContext } from '../contexts/SocketContext.jsx'; // ADD
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { createOrderFromQrMenu, getCart, setCart } from '../controllers/qrmenu.controller';
import { getImageURL } from '../helpers/ImageHelper';
import { iconStroke } from '../config/config';

import { 
  IconChevronRight, IconCarrot, IconTrash, IconMinus, IconPlus, 
  IconShoppingCartX, IconChevronLeft, IconX, IconShovelPitchforks, IconNote, IconBed, IconMapPin
} from '@tabler/icons-react';

const CartPage = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { socket, isConnected } = useContext(SocketContext); // ADD

  const [state, setState] = useState({ cartItems: [], itemsTotal: 0, taxTotal: 0, serviceChargeTotal: 0, payableTotal: 0 });
  const [showPhoneFields, setShowPhoneFields] = useState(false);
  const [selectedCustomerType, setSelectedCustomerType] = useState(null); // ADD

  const params = useParams();
  const qrcode = params.qrcode;
  const navigate = useNavigate();
  const location = useLocation();
  const { storeTable, currency, serviceCharge, room } = location.state || {};

  const nameRef = useRef(null);
  const phoneRef = useRef(null);
  const dialogNotesIndexRef = useRef(null);
  const dialogNotesTextRef = useRef(null);

  const [deliveryTypeSelection, setDeliveryTypeSelection] = useState(room ? "room_service" : (storeTable ? "dine_in" : "delivery"));
  const [roomNumberInput, setRoomNumberInput] = useState(room || "");
  const [deliveryAddressInput, setDeliveryAddressInput] = useState("");
  const [deliveryPhoneInput, setDeliveryPhoneInput] = useState("");
  const [gps, setGps] = useState({ latitude: null, longitude: null });
  const [gpsError, setGpsError] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);

  const fetchGpsLocation = () => {
    if (!navigator.geolocation) {
      setGpsError("Geolocation is not supported by your browser");
      return;
    }
    setGpsLoading(true);
    setGpsError(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGps({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
        setGpsLoading(false);
        toast.success("GPS Location fetched successfully!");
      },
      (error) => {
        console.error(error);
        setGpsLoading(false);
        setGpsError("Failed to retrieve GPS location. Please make sure location access is enabled.");
        toast.error("Failed to retrieve GPS location");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  useEffect(() => {
    if (deliveryTypeSelection === 'delivery') {
      fetchGpsLocation();
    }
  }, [deliveryTypeSelection]);
  // ... rest of your code
  /* --------------------------------------------------------------- */
  /* 1. Load cart on mount                                            */
  /* --------------------------------------------------------------- */
  useEffect(() => {
    const storedCart = getCart() || [];
    const summary = calculateOrderSummary(storedCart);
    setState(prev => ({ ...prev, cartItems: storedCart, ...summary }));
  }, []);

  const { cartItems, itemsTotal, taxTotal, serviceChargeTotal, payableTotal } = state;

  /* --------------------------------------------------------------- */
  /* 2. Emit new QR order to backend                                  */
  /* --------------------------------------------------------------- */
  const sendNewOrderEvent = () => {
    const socketInstance = socket;
    if (socketInstance?.connected || isSocketConnected) {
      socketInstance.emit('new_qrorder_backend', {}, qrcode);
    }
  };

  /* --------------------------------------------------------------- */
  /* 3. Cart operations                                               */
  /* --------------------------------------------------------------- */
  const removeItemFromCart = (index) => {
    const newCart = cartItems.filter((_, i) => i !== index);
    setCart(newCart);
    updateCart(newCart);
  };

  const addCartItemQuantity = (index, currentQty) => {
    const newQty = currentQty + 1;
    const newCart = [...cartItems];
    newCart[index].quantity = newQty;
    setCart(newCart);
    updateCart(newCart);
  };

  const minusCartItemQuantity = (index, currentQty) => {
    const newQty = currentQty - 1;
    let newCart = [...cartItems];
    if (newQty <= 0) {
      newCart = newCart.filter((_, i) => i !== index);
    } else {
      newCart[index].quantity = newQty;
    }
    setCart(newCart);
    updateCart(newCart);
  };

  const updateCart = (items) => {
    const summary = calculateOrderSummary(items);
    setState(prev => ({ ...prev, cartItems: items, ...summary }));
  };

  /* --------------------------------------------------------------- */
  /* 4. Order summary calculation                                     */
  /* --------------------------------------------------------------- */
  const calculateOrderSummary = (items) => {
    let itemsTotal = 0, taxTotal = 0, serviceChargeTotal = 0, payableTotal = 0;

    items.forEach(item => {
      const taxRate = Number(String(item.tax_rate || 0).replace(/,/g, ""));
      const taxType = item.tax_type;
      const linePrice = Number(String(item.price).replace(/,/g, "")) * Number(String(item.quantity).replace(/,/g, ""));

      if (taxType === 'exclusive') {
        const tax = (linePrice * taxRate) / 100;
        taxTotal += tax;
        itemsTotal += linePrice;
        payableTotal += linePrice + tax;
      } else if (taxType === 'inclusive') {
        const tax = linePrice - (linePrice * (100 / (100 + taxRate)));
        taxTotal += tax;
        itemsTotal += linePrice - tax;
        payableTotal += linePrice;
      } else {
        itemsTotal += linePrice;
        payableTotal += linePrice;
      }
    });

    if (serviceCharge) {
      const sc = (itemsTotal * Number(String(serviceCharge).replace(/,/g, ""))) / 100;
      serviceChargeTotal += sc;
      payableTotal += sc;
    }

    return { itemsTotal, taxTotal, serviceChargeTotal, payableTotal };
  };

  /* --------------------------------------------------------------- */
  /* 5. Place order                                                   */
  /* --------------------------------------------------------------- */
  const btnPlaceOrder = async () => {
    let name = "Guest";
    let phone = "";

    if (showPhoneFields) {
      name = nameRef.current?.value?.trim() || "";
      phone = phoneRef.current?.value?.trim() || "";

      if (!name) return toast.error(t('cart.name_required'));
      if (!validatePhone(phone)) return toast.error(t('cart.valid_phone_error'));
    }

    if (deliveryTypeSelection === 'room_service' && !roomNumberInput.trim()) {
      return toast.error("Room Number is required for Room Service");
    }

    if (deliveryTypeSelection === 'delivery') {
      if (!deliveryAddressInput.trim()) {
        return toast.error("Delivery Address is required");
      }
      if (!deliveryPhoneInput.trim()) {
        return toast.error("Contact phone number is required for delivery");
      }
    }

    const deliveryType = deliveryTypeSelection;
    const tableId = deliveryTypeSelection === 'dine_in' ? (storeTable?.id || null) : null;
    const source = deliveryTypeSelection === 'room_service' ? `Room ${roomNumberInput}` : null;
    const customerType = showPhoneFields ? "CUSTOMER" : "WALKIN";
    const customer = showPhoneFields
      ? { name, phone }
      : { name: "Guest", phone: "" };

    toast.loading(t('cart.please_wait'));

    try {
      const res = await createOrderFromQrMenu(
        deliveryType, 
        cartItems, 
        customerType, 
        customer, 
        tableId, 
        qrcode, 
        source,
        deliveryAddressInput,
        deliveryPhoneInput,
        gps.latitude,
        gps.longitude
      );
      toast.dismiss();

      if (res.status === 200) {
        toast.success(t('cart.order_success'));
        document.getElementById("modal-place-order").close();

        setCart([]);
        setState(prev => ({ ...prev, cartItems: [] }));
        setSelectedCustomerType(null);
        sendNewOrderEvent();

        navigate('/m/order-success');
      }
    } catch (error) {
      const message = error?.response?.data?.message || t('cart.something_went_wrong');
      toast.dismiss();
      toast.error(message);
      navigate('/m/order-failed');
    }
  };

  /* --------------------------------------------------------------- */
  /* 6. Notes modal                                                   */
  /* --------------------------------------------------------------- */
  const btnOpenNotesModal = (index, notes) => {
    dialogNotesIndexRef.current.value = index;
    dialogNotesTextRef.current.value = notes || '';
    document.getElementById('modal-notes').showModal();
  };

  const btnAddNotes = () => {
    const index = Number(String(dialogNotesIndexRef.current.value).replace(/,/g, ""));
    const notes = dialogNotesTextRef.current.value || null;
    const newCart = [...cartItems];
    newCart[index].notes = notes;
    setCart(newCart);
    updateCart(newCart);
    document.getElementById('modal-notes').close();
  };



  return (
    <div className='w-full'>
      <div className='container w-full max-w-5xl mx-auto flex justify-center items-center'>
          <div className="w-full mx-auto max-w-5xl bg-gray-50 dark:bg-black rounded-xl p-4 h-screen border border-restro-green-light"> {/* Centered cart container */}
          <div className="relative flex items-center justify-between p-4shadow-md">
          {/* Back Button */}
          <button className="absolute left-0 p-2" onClick={() => window.history.back()}>
            <IconChevronLeft size={24} stroke={2} /> {/* Back arrow icon */}
          </button>

          {/* Centered Title */}
          <h3 className="text-xl md:text-2xl font-bold text-center w-full">{t('cart.title')}</h3>
        </div>


        {room && (
          <div className="flex items-center gap-2 mt-4 p-2 justify-center bg-white dark:bg-black rounded-lg shadow-sm">
            <IconBed size={20} stroke={2} className="text-gray-700" />
            <h3 className="text-lg font-semibold">
              <span className="font-bold text-gray-800">Room {room}</span>
            </h3>
            <p className="text-md text-gray-600">
              <span className="font-semibold">(Room Service)</span>
            </p>
          </div>
        )}

        {!room && storeTable && (
          <div className="flex items-center gap-2 mt-4 p-2 justify-center bg-white dark:bg-black rounded-lg shadow-sm">
            <IconShovelPitchforks size={20} stroke={2} className="text-gray-700" />

            <h3 className="text-lg font-semibold">
              <span className="font-bold text-gray-800">{storeTable.table_title}</span>
            </h3>

            {storeTable.floor && (
              <p className="text-md text-gray-600">
                <span className="font-semibold">( {storeTable.floor})</span>
              </p>
            )}
          </div>
        )}


        {/* Cart Items */}
        <div className="mt-4">
          {cartItems.length > 0 ? (
            cartItems.map((item, i) => {
              const { id, title, price, quantity, image , notes} = item;
              const imageURL = getImageURL(image);

              return (
                <div key={i} className="w-full bg-white dark:bg-black rounded-lg p-2 mb-2 md:mb-4 flex items-center justify-between">
                  <div className="flex items-start">
                    <div className="w-20 h-20 md:w-28 md:h-28 bg-gray-200 dark:bg-black rounded-lg overflow-hidden mr-4 md:mr-6 object-cover flex items-center justify-center text-gray-500 flex-shrink-0">
                      {image ? (
                        <img src={imageURL} alt={title} className="object-cover w-full h-full" />
                      ) : (
                        <IconCarrot size={24} />
                      )}
                    </div>
                    <div className="flex-grow">
                      <p className="text-sm font-semibold md:text-lg line-clamp-2 text-ellipsis">{title}</p>
                      <div className="text-xs text-gray-400 md:text-sm">
                       {item?.variant?.title && <p>{item.variant.title}</p>}
                        {item.addons?.length > 0 && <p>{item.addons.length} Addons</p>}
                      </div>
                      <p className="text-sm md:text-lg">{currency}{(price * quantity).toFixed(2)}</p>
                      {notes && <p className='text-xs text-gray-500 md:text-sm'>{t('cart.notes')}: {notes}</p>}
                    </div>
                  </div>
                  <div className="flex flex-col items-center gap-2">
                  <div className="flex items-center">
                    <button
                      onClick={() => minusCartItemQuantity(i, quantity)}
                      className="btn btn-square btn-xs md:btn-sm rounded-lg"
                    >
                      <IconMinus stroke={iconStroke} size={14}/>
                    </button>

                    <div className="text-center text-sm w-6 md:text-xl rounded-lg">{quantity}</div>

                    <button
                      onClick={() => addCartItemQuantity(i, quantity)}
                      className="btn btn-square btn-xs md:btn-sm rounded-lg"
                    >
                      <IconPlus stroke={iconStroke} size={14} />
                    </button>

                    <button
                      onClick={() => removeItemFromCart(i)}
                      className="text-restro-red hover:text-red-700 transition ml-4 w-4 h-4 md:w-8 md:h-8"
                    >
                      <IconTrash stroke={iconStroke} className='w-full h-full'/>
                    </button>
                  </div>

                  <button
                    onClick={() => {btnOpenNotesModal(i, notes)}}
                    className="text-sm md:text-md transition active:scale-95 hover:shadow-lg px-2 py-1 flex md:px-4 md:py-1 items-center gap-1 mt-4 ml-auto rounded-xl border border-restro-border-green bg-restro-card-bg hover:bg-restro-button-hover text-restro-text' "
                  >
                    <IconNote size={18} stroke={iconStroke} />
                    <p className='text-sm md:text-lg'>{t('cart.add_notes')}</p>
                  </button>
                </div>


                </div>
              );
            })
          ) : (
            <div className="text-center text-gray-500 flex flex-col justify-center items-center h-[85vh] gap-4">
              <IconShoppingCartX size={50} />
              <p className='text-xl'>{t('cart.empty_cart')}</p>
            </div>
          )}
        </div>

        {/* Order Summary Section */}
        {cartItems.length > 0 && (
          <div className="join join-vertical w-full">
            <div className="collapse collapse-arrow join-item bg-white dark:bg-black">
              <input type="checkbox" className="peer" />
              <div className="collapse-title text-lg md:text-xl font-bold flex justify-between">
                <span>{t('cart.payable_total')}</span>
                <span className='text-restro-green font-bold'>{currency}{payableTotal.toFixed(2)}</span>
              </div>
              <div className="collapse-content space-y-2 text-sm md:text-md">
                <div className="flex justify-between">
                  <span>{t('cart.items_total')}</span>
                  <span>{currency}{itemsTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('cart.tax_total')}</span>
                  <span>{currency}{taxTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('cart.service_charge_total')}</span>
                  <span>{currency}{serviceChargeTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

      <div className="h-28"/>

        {/* Checkout Section */}
        {cartItems.length > 0 && (
          <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-black shadow-lg p-4 flex justify-between items-center rounded-2xl max-w-5xl mx-auto">
            <button
            onClick={() => {
              document.getElementById('modal-place-order').showModal();
            }}
            className="bg-restro-green text-white py-4 px-6 rounded-xl flex justify-between w-full items-center">
              <p className="text-md font-bold md:text-lg">{t('cart.total')} {currency}{payableTotal.toFixed(2)}</p>
              <p className="text-white text-lg md:text-xl font-bold px-2 rounded-lg flex items-center">
                {t('cart.checkout')} <IconChevronRight size={20} stroke={3} />
              </p>
            </button>
          </div>
        )}
      </div>


      <dialog id="modal-place-order" className="modal modal-bottom sm:modal-middle w-full mx-auto max-w-5xl">
        <div className='modal-box border border-restro-border-green dark:rounded-2xl'>

          <div className="my-2 mx-auto w-full relative">
            <h3 className='text-center text-xl font-semibold'>{t('cart.continue_as')}</h3>

            <div className="absolute top-0 right-0 text-red-400 cursor-pointer">
              <button onClick={() => {document.getElementById('modal-place-order').close()}}>
                <IconX size={20} />
              </button>
            </div>

            <div className="flex flex-col justify-around mt-4">
              <button
                className={`rounded-lg transition px-4 py-3 text-lg font-semibold  border-2 ${
                  !showPhoneFields ? 'bg-white dark:bg-black border-restro-green text-restro-green shadow-sm' : 'bg-gray-100 dark:bg-black text-gray-600 hover:bg-gray-200'
                }`}
                onClick={() => setShowPhoneFields(false)}
              >
                {t('cart.guest')}
              </button>

              <div className="relative flex items-center justify-center my-6">
                <div className="border-t border-restro-border-green w-full"></div>
                <span className="absolute bg-white dark:bg-black px-4 text-gray-500 text-sm">{t('cart.or')}</span>
              </div>

              <button
                className={`rounded-lg transition px-4 py-3 text-lg font-semibold  border-2 ${
                  showPhoneFields ? 'bg-white dark:bg-black border-restro-green text-restro-green shadow-sm ' : 'bg-gray-100 dark:bg-black text-restro-text hover:bg-gray-200 border-restro-border-green'
                }`}
                onClick={() => setShowPhoneFields(true)}
              >
                {t('cart.using_phone_number')}
              </button>
            </div>

            {showPhoneFields && (
              <div className="mt-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">{t('cart.name_label')} <span className="text-xs text-gray-400">- (Required)</span></label>
                  <input
                    type="text"
                    ref={nameRef}
                    className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green'
                    placeholder={t('cart.name_placeholder')}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">{t('cart.phone_label')} <span className="text-xs text-gray-400">- (Required)</span></label>
                  <input
                    type="tel"
                    ref={phoneRef}
                    className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green'
                    placeholder={t('cart.phone_placeholder')}
                  />
                </div>
              </div>
            )}

            {/* Delivery Type Option Selector */}
            <div className="mt-6 border-t border-gray-100 dark:border-zinc-800 pt-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Delivery Option</label>
              <div className="grid grid-cols-3 gap-2">
                {storeTable && (
                  <button
                    type="button"
                    onClick={() => setDeliveryTypeSelection("dine_in")}
                    className={`py-2 px-3 text-xs font-bold rounded-xl border transition ${
                      deliveryTypeSelection === 'dine_in' 
                        ? 'bg-green-600 text-white border-green-600' 
                        : 'bg-gray-100 dark:bg-zinc-900 text-gray-600 border-gray-200 dark:border-zinc-800 hover:bg-gray-200'
                    }`}
                  >
                    Dine In
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setDeliveryTypeSelection("room_service")}
                  className={`py-2 px-3 text-xs font-bold rounded-xl border transition ${
                    deliveryTypeSelection === 'room_service' 
                      ? 'bg-green-600 text-white border-green-600' 
                      : 'bg-gray-100 dark:bg-zinc-900 text-gray-600 border-gray-200 dark:border-zinc-800 hover:bg-gray-200'
                  }`}
                >
                  Room Service
                </button>
                <button
                  type="button"
                  onClick={() => setDeliveryTypeSelection("delivery")}
                  className={`py-2 px-3 text-xs font-bold rounded-xl border transition ${
                    deliveryTypeSelection === 'delivery' 
                      ? 'bg-green-600 text-white border-green-600' 
                      : 'bg-gray-100 dark:bg-zinc-900 text-gray-600 border-gray-200 dark:border-zinc-800 hover:bg-gray-200'
                  }`}
                >
                  Home Delivery
                </button>
              </div>
            </div>

            {/* Room Service Fields */}
            {deliveryTypeSelection === "room_service" && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700">Room Number <span className="text-xs text-gray-400">- (Required)</span></label>
                <input
                  type="text"
                  value={roomNumberInput}
                  onChange={(e) => setRoomNumberInput(e.target.value)}
                  className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green mt-1'
                  placeholder="Enter your room number (e.g. 101)"
                />
              </div>
            )}

            {/* Delivery Fields */}
            {deliveryTypeSelection === "delivery" && (
              <div className="mt-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Delivery Address <span className="text-xs text-gray-400">- (Required)</span></label>
                  <textarea
                    rows={2}
                    value={deliveryAddressInput}
                    onChange={(e) => setDeliveryAddressInput(e.target.value)}
                    className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green mt-1'
                    placeholder="Enter full delivery address, building, apartment etc."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Contact Number for Delivery <span className="text-xs text-gray-400">- (Required)</span></label>
                  <input
                    type="tel"
                    value={deliveryPhoneInput}
                    onChange={(e) => setDeliveryPhoneInput(e.target.value)}
                    className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green mt-1'
                    placeholder="Enter phone number"
                  />
                </div>
                
                {/* GPS Coordinates Fetching */}
                <div className="bg-gray-50 dark:bg-zinc-900/60 p-3 rounded-2xl border border-gray-100 dark:border-zinc-800">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-gray-600 dark:text-gray-400">GPS Location</span>
                    <button
                      type="button"
                      onClick={fetchGpsLocation}
                      disabled={gpsLoading}
                      className="text-xs bg-green-500 hover:bg-green-600 text-white font-bold px-3 py-1.5 rounded-xl flex items-center gap-1 disabled:opacity-50 transition active:scale-95"
                    >
                      <IconMapPin size={12} />
                      {gpsLoading ? 'Fetching...' : 'Get GPS Location'}
                    </button>
                  </div>
                  
                  {gps.latitude && gps.longitude ? (
                    <p className="text-[11px] font-mono text-green-600 dark:text-green-400 mt-2">
                      Latitude: {gps.latitude.toFixed(6)}, Longitude: {gps.longitude.toFixed(6)}
                    </p>
                  ) : (
                    <p className="text-[11px] text-amber-500 mt-2">
                      {gpsError || "Please fetch GPS coordinates to help delivery driver find you quickly."}
                    </p>
                  )}
                </div>
              </div>
            )}

          </div>

          <div className="modal-action justify-center w-full">
            <button
              onClick={() => {btnPlaceOrder()}}
              className="rounded-lg hover:bg-restro-green-dark transition active:scale-95 hover:shadow-lg px-4 py-4 bg-restro-green text-white w-full text-xl font-semibold"
            >
              {t('cart.place_order')}
            </button>
          </div>
        </div>
      </dialog>


      {/* dialog: notes */}
      <dialog id="modal-notes" className="modal modal-bottom sm:modal-middle">
        <div className='modal-box border border-restro-border-green dark:rounded-2xl'>
          <h3 className="font-bold text-lg">{t('cart.add_notes')}</h3>

          <div className="my-4">
            <input type="hidden" ref={dialogNotesIndexRef} />
            <label htmlFor="dialogNotesText" className="mb-1 block text-gray-500 text-sm">{t('cart.notes')} <span className="text-xs text-gray-500">(100 character max.)</span></label>
            <input ref={dialogNotesTextRef} type="text" name="dialogNotesText" id='dialogNotesText' className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green' placeholder={t('cart.notes_placeholder')} />
          </div>

          <div className="modal-action">
            <form method="dialog">
              {/* if there is a button in form, it will close the modal */}
              <button className="rounded-lg transition active:scale-95 hover:shadow-lg px-4 py-3 bg-restro-gray hover:bg-restro-button-hover text-restro-text">{t('cart.close')}</button>
              <button onClick={()=>{btnAddNotes();}} className="rounded-lg hover:bg-green-800 transition active:scale-95 hover:shadow-lg px-4 py-3 bg-restro-green text-white ml-3">{t('cart.save')}</button>
            </form>
          </div>
        </div>
      </dialog>
      {/* dialog: notes */}


      </div>

    </div>
  );
};

export default CartPage;
